import { useEffect, useRef, useState } from "react";
import styles from "./StopLossFilters.module.css";
import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import arrowUpIcon from "../../../../assets/icons/arrow_up_icon.svg";
import arrowDownIcon from "../../../../assets/icons/down_arrow.svg";

import {
  FiltersCampaignDataQuadrant,
  FiltersDimensionDataQuadrant,
  FiltersMeasuresDataQuadrant,
  useTopXDataConfig,
} from "../../../../components/Filter/FilterData/quadrantFilter";
import CustomTextField from "../../../../components/Filter/subComponents/CustomTextField/CustomTextField";
import { processFields } from "../../../../components/Filter/subComponents/filterHelper";
import { useGlobalContext } from "../../../../context/global";
import { ALL_WEEK_DAYS } from "../../../../helper/Quadrant/quadrantHelper";
import ComplexFilterWidget from "../../../../components/Filter/ComplexFilterWidget";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";

export default function StopLossFilters(props) {
  const { clientId } = useGlobalContext();
  const {
    pslRuleId,
    handleStoplossRuleDetail,
    stopLossFilterType = "exclusion",
    onStopLossFilterTypeChange,
  } = props;
  const TopXDataConfig = useTopXDataConfig();

  const userPlatform = JSON.parse(localStorage.getItem("user_platform"));
  const [expandFilters, setExpandFilters] = useState(true);
  const STOP_LOSS_DATE_OPTIONS = [
    { value: "last_7d", label: "Last 7 days", type: "" },
    { value: "last_14d", label: "Last 14 days", type: "" },
    { value: "last_30d", label: "Last 30 days", type: "" },
  ];
  const [selectedDay, setSelectedDay] = useState(
    STOP_LOSS_DATE_OPTIONS.find((option) => option.value === props.customDate)
  );
  const [inclusionSelectedDay, setInclusionSelectedDay] = useState(
    STOP_LOSS_DATE_OPTIONS.find(
      (option) => option.value === props.inclusionCustomDate
    )
  );
  const [stoplossRuleDetail, setStoplossRuleDetail] = useState({
    ...props.stoplossRuleDetail,
  });
  const [ruleNameInput, setRuleNameInput] = useState(
    props.stoplossRuleDetail?.name ?? ""
  );
  const ruleNameDebounceRef = useRef(null);
  const [errors, setErrors] = useState(props?.errors);

  const isExclusionTab = stopLossFilterType === "exclusion";
  const isRuleTypeLocked = Boolean(pslRuleId);
  const activeFilterModuleFilters = isExclusionTab
    ? props.filterModuleFilters
    : props.inclusionFilterModuleFilters;

  const exclusionPercentOptions = [
    { label: "10%", value: 10 },
    { label: "20%", value: 20 },
    { label: "30%", value: 30 },
    { label: "40%", value: 40 },
    { label: "50%", value: 50 },
    { label: "60%", value: 60 },
    { label: "70%", value: 70 },
    { label: "80%", value: 80 },
    { label: "90%", value: 90 },
    { label: "100%", value: 100 },
  ];

  const frequencyOptions = [
    { label: "Daily", value: "Daily" },
    { label: "Weekly", value: "Weekly" },
    { label: "Bi-weekly", value: "Biweekly" },
    { label: "Custom", value: "Custom" },
  ];

  useEffect(() => {
    setSelectedDay(
      STOP_LOSS_DATE_OPTIONS.find((option) => option.value === props.customDate)
    );
  }, [props.customDate]);

  useEffect(() => {
    setInclusionSelectedDay(
      STOP_LOSS_DATE_OPTIONS.find(
        (option) => option.value === props.inclusionCustomDate
      )
    );
  }, [props.inclusionCustomDate]);

  useEffect(() => {
    setStoplossRuleDetail({ ...props.stoplossRuleDetail });
  }, [props.stoplossRuleDetail]);

  useEffect(() => {
    setRuleNameInput(props.stoplossRuleDetail?.name ?? "");
  }, [props.stoplossRuleDetail?.name]);

  useEffect(() => {
    setErrors(props.errors);
  }, [props.errors]);

  useEffect(() => {
    if (pslRuleId) return;
    if (ruleNameDebounceRef.current) {
      clearTimeout(ruleNameDebounceRef.current);
    }
    ruleNameDebounceRef.current = setTimeout(() => {
      handleStoplossRuleDetail?.("name", ruleNameInput);
    }, 250);

    return () => {
      if (ruleNameDebounceRef.current) {
        clearTimeout(ruleNameDebounceRef.current);
      }
    };
  }, [ruleNameInput, pslRuleId, handleStoplossRuleDetail]);

  const isFiltersApplied = (filterModuleFilters) => {
    return (
      Object.keys(filterModuleFilters?.metrics?.custom_output || {})?.length >
        0 ||
      Object.keys(filterModuleFilters?.dimension?.custom_output || {})?.length >
        0 ||
      Object.keys(filterModuleFilters?.campaigns?.custom_output || {})
        ?.length > 0 ||
      Object.keys(filterModuleFilters?.topX?.custom_output || {})?.length > 0
    );
  };

  useEffect(() => {
    const init = async () => {
      const precessedDimensionData = await processFields(
        FiltersDimensionDataQuadrant[userPlatform].fields,
        clientId,
        []
      );
      FiltersDimensionDataQuadrant[userPlatform].fields = precessedDimensionData;
      const precessedMeasuresData = await processFields(
        FiltersMeasuresDataQuadrant[userPlatform].fields,
        clientId,
        []
      );
      FiltersMeasuresDataQuadrant[userPlatform].fields = precessedMeasuresData;
      const precessedAmazonData = await processFields(
        FiltersCampaignDataQuadrant[userPlatform].fields,
        clientId,
        []
      );
      FiltersCampaignDataQuadrant[userPlatform].fields = precessedAmazonData;
    };
    init();
  }, [clientId]);

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: "10px",
      }}
    >
      <Box sx={{ mt: 4 }}>
        <Stack direction="column" spacing={1}>
          <Typography
            variant="body2"
            sx={{ color: "#374151", fontWeight: 700, fontSize: "16px" }}
          >
            Select Rule type
          </Typography>
          <RadioGroup
            row
            value={stopLossFilterType}
            onChange={(e) => onStopLossFilterTypeChange?.(e.target.value)}
            aria-label="stop loss filter type"
            sx={{ gap: 2, ml: 0.5 }}
          >
            <FormControlLabel
              value="exclusion"
              disabled={isRuleTypeLocked}
              control={<Radio size="small" sx={{ color: "#6759FF", "&.Mui-checked": { color: "#6759FF" } }} />}
              label={
                <Typography variant="body2" sx={{ color: "#374151" }}>
                  Exclusion Rule
                </Typography>
              }
            />
            <FormControlLabel
              value="inclusion"
              disabled={isRuleTypeLocked}
              control={<Radio size="small" sx={{ color: "#6759FF", "&.Mui-checked": { color: "#6759FF" } }} />}
              label={
                <Typography variant="body2" sx={{ color: "#374151" }}>
                  Inclusion Rule
                </Typography>
              }
            />
          </RadioGroup>
        </Stack>
      </Box>

      <div className={styles.pageFilterWrapper}>
        <div
          style={{
            display: "flex",
            padding: expandFilters ? "12px 24px" : "12px 24px",
            justifyContent: "space-between",
            borderBottom: expandFilters ? "1px solid #C9CCCF" : "0px",
            cursor: "pointer",
            backgroundColor: "#FAFBFB",
          }}
          onClick={() => setExpandFilters(!expandFilters)}
        >
          <div
            style={{
              fontSize: "16px",
              lineHeight: "1.3",
              color: "#000",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontWeight: 600,
            }}
          >
            <FilterAltOutlinedIcon sx={{ fontSize: 18, color: "#6d7175" }} />
            {isExclusionTab ? "Exclusion Filters" : "Inclusion Filters"}
            <Chip
              label={
                <Typography fontSize="10px" variant="subtitle2">
                  {isFiltersApplied(activeFilterModuleFilters)
                    ? "Applied"
                    : "Not Applied"}
                </Typography>
              }
              sx={{
                backgroundColor: isFiltersApplied(activeFilterModuleFilters)
                  ? "#dde0e4"
                  : "#E5E7EB",
                color: isFiltersApplied(activeFilterModuleFilters)
                  ? "#1f2124"
                  : "#6d7175",
                height: "22px",
                borderRadius: "12px",
                padding: "0 6px",
                "& .MuiChip-label": {
                  padding: 0,
                },
              }}
            />
          </div>
          <div>
            <img
              height={10}
              src={expandFilters ? arrowUpIcon : arrowDownIcon}
              alt="up"
            />
          </div>
        </div>

        <div
          style={{
            opacity: expandFilters ? 1 : 0,
            transform: expandFilters ? "scaleY(1)" : "scaleY(0)",
            transformOrigin: "center",
            transition: "opacity 0.3s ease-in-out, transform 0.2s ease-in-out",
            visibility: expandFilters ? "visible" : "hidden",
            maxHeight: expandFilters ? "max-content" : "0",
          }}
        >
          <ComplexFilterWidget
            key={stopLossFilterType}
            config={{
              campaigns: FiltersCampaignDataQuadrant[userPlatform],
              measure: FiltersMeasuresDataQuadrant[userPlatform],
              dimension: FiltersDimensionDataQuadrant[userPlatform],
              topXConfig: TopXDataConfig,
            }}
            summaryStyles={{ border: "none", paddingTop: "10px" }}
            filters={activeFilterModuleFilters}
            output={
              isExclusionTab
                ? props.handleFilterOutput
                : props.handleInclusionFilterOutput
            }
            prefixMember={""}
            type="page"
            page={"stoploss"}
            showPopupModal={expandFilters}
            setShowPopupModal={setExpandFilters}
            fetchingData={props.fetchingData}
            isAppliedCallback={props.handleFilterApplied}
            narrowFilters={true}
            daysOptions={STOP_LOSS_DATE_OPTIONS}
            selectedDay={isExclusionTab ? selectedDay : inclusionSelectedDay}
            sidebar={true}
            default={"metrics"}
            showDays={true}
            id={stopLossFilterType}
            campaignFilter={false}
            handleSelectedDay={
              isExclusionTab
                ? props.handleCustomDate
                : props.handleInclusionCustomDate
            }
            calloutTitle="Select your filters"
            calloutContent="+ Add Filter"
            hideFilterSummary={true}
          />
        </div>
      </div>

      <div className={styles.pageFilterWrapper}>
        <div>
          <Box
            className={styles.pageContent}
            sx={{ display: "grid", gap: 2, p: 1 }}
          >
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack spacing={2}>
                <Typography variant="subtitle1" fontWeight={700}>
                  Configuration
                </Typography>

                <Box sx={{ maxWidth: 560 }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, color: "#222" }}>
                    Rule Name
                  </Typography>
                  <CustomTextField
                    placeholder={"Enter Rule Name"}
                    value={ruleNameInput}
                    type="text"
                    error={(errors?.name ?? "") ? true : false}
                    disabled={pslRuleId ? true : false}
                    onChange={(value) => setRuleNameInput(value)}
                    onBlur={() =>
                      handleStoplossRuleDetail?.("name", ruleNameInput)
                    }
                  />
                  {errors?.name && (
                    <Typography variant="body2" sx={{ color: "#FF4D4F" }}>
                      {errors?.name}
                    </Typography>
                  )}
                </Box>

                {isExclusionTab && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1.5}
                    flexWrap="wrap"
                  >
                    <Typography variant="body2" sx={{ color: "#374151" }}>
                      Don&apos;t exclude if total exclusion is greater than
                    </Typography>
                    <Box sx={{ width: 120 }}>
                      <FormControl fullWidth size="small">
                        <Select
                          value={stoplossRuleDetail?.max_percent_exclude ?? ""}
                          displayEmpty
                          onChange={(e) =>
                            props?.handleStoplossRuleDetail(
                              "max_percent_exclude",
                              e.target.value === ""
                                ? null
                                : Number(e.target.value),
                            )
                          }
                          sx={{
                            fontSize: "14px",
                            height: 36,
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor:
                                (errors?.max_percent_exclude ?? "")
                                  ? "#FF4D4F"
                                  : "#AEB4B9",
                            },
                          }}
                        >
                          {exclusionPercentOptions
                            .filter((option) => option.value !== null)
                            .map((option) => (
                              <MenuItem
                                key={option.value}
                                value={option.value}
                                sx={{ fontSize: "14px" }}
                              >
                                {option.label}
                              </MenuItem>
                            ))}
                        </Select>
                      </FormControl>
                    </Box>
                    <Typography variant="body2" sx={{ color: "#374151" }}>
                      of products
                    </Typography>
                  </Stack>
                )}

                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1.5}
                  flexWrap="wrap"
                >
                  <Typography variant="body2" sx={{ color: "#374151" }}>
                    Product Stop Loss running schedule will be
                  </Typography>
                  <Box sx={{ minWidth: 180, maxWidth: 230 }}>
                    <FormControl fullWidth size="small">
                      <Select
                        value={stoplossRuleDetail?.frequency ?? ""}
                        displayEmpty
                        onChange={(e) =>
                          props?.handleStoplossRuleDetail(
                            "frequency",
                            e.target.value,
                          )
                        }
                        sx={{
                          fontSize: "14px",
                          height: 36,
                          "& .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#AEB4B9",
                          },
                        }}
                      >
                        {frequencyOptions.map((option) => (
                          <MenuItem
                            key={option.value}
                            value={option.value}
                            sx={{ fontSize: "14px" }}
                          >
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Stack>

                <Box className={styles.daysFrequency}>
                  <Typography
                    className={styles.lineClass}
                    sx={{ color: "black", mb: 1 }}
                  >
                    Repeat On
                  </Typography>
                  {ALL_WEEK_DAYS.map((day, index) => (
                    <div
                      key={index}
                      className={`checkbox_frequncy ${
                        stoplossRuleDetail?.stoploss_running_days?.includes(day)
                          ? styles.selectedCircle
                          : styles.unselectedCircle
                      }`}
                      onClick={() => {
                        if (stoplossRuleDetail?.frequency === "Daily") {
                          return;
                        }
                        props?.handleStoplossRuleDetail(
                          "stoploss_running_days",
                          day,
                        );
                      }}
                    >
                      {day.charAt(0).toUpperCase() + day.slice(1)}
                    </div>
                  ))}
                </Box>

                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Checkbox
                      checked={stoplossRuleDetail?.recommend_only === true}
                      onChange={(e) =>
                        props?.handleStoplossRuleDetail(
                          "recommend_only",
                          e.target.checked,
                        )
                      }
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: "#374151" }}>
                      Don&apos;t apply Stop Loss, only give recommendations
                    </Typography>
                  }
                />
              </Stack>
            </Paper>

            {props?.dateOffset && props.dateOffset > 1 && (
              <Paper
                variant="outlined"
                sx={{
                  p: 1.5,
                  borderRadius: 2,
                  bgcolor: "#fafafa",
                  color: "#6d7175",
                }}
              >
                <Stack direction="row" spacing={0.5} alignItems="flex-start">
                  <Typography variant="body2">*</Typography>
                  <Typography variant="body2">
                    Data picked up for the creation of Product Stop Loss could
                    vary according to the last refreshed at date of the source
                    data.
                  </Typography>
                </Stack>
              </Paper>
            )}
          </Box>
        </div>
      </div>
    </div>
  );
}
